import React, { useEffect } from "react";
import type { FoodSpot } from "../types";

interface Props {
  spot: FoodSpot | null;
  onClose: () => void;
}

const SpotDetail: React.FC<Props> = ({ spot, onClose }) => {
  useEffect(() => {
    if (!spot) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [spot, onClose]);

  if (!spot) return null;

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${spot.name}, ${spot.area}, Kozhikode, Kerala`,
  )}&query_place_id=${encodeURIComponent(spot.id)}`;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="spot-modal-title"
    >
      <div className="modal">
        <header className="modal__head">
          <button
            className="modal__close"
            onClick={onClose}
            aria-label="Close details"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
          <p className="modal__type">
            <i className="fa-solid fa-utensils" aria-hidden="true" />
            {spot.type}
          </p>
          <h3 id="spot-modal-title" className="modal__title">
            {spot.name}
          </h3>
          <p className="modal__area">
            <i className="fa-solid fa-location-dot" aria-hidden="true" />
            {spot.area} &middot; {spot.cuisine}
          </p>
        </header>

        <div className="modal__body">
          <div className="modal__rating-row">
            <span className="modal__rating">
              <i className="fa-solid fa-star" aria-hidden="true" />
              {spot.rating.toFixed(1)}
            </span>
            <span className="modal__price">
              {spot.priceRange} {spot.priceRange.length === 1 ? "budget" : spot.priceRange.length === 2 ? "mid-range" : "premium"}
            </span>
            <span className="modal__hours">
              <i className="fa-regular fa-clock" aria-hidden="true" />
              {spot.hours}
            </span>
          </div>

          <div className="modal__section">
            <h4>About</h4>
            <p className="modal__desc">{spot.description}</p>
          </div>

          <div className="modal__section">
            <h4>Specialties</h4>
            <div className="modal__specs">
              {spot.specialties.map((s) => (
                <span key={s} className="spec-tag">
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="modal__section">
            <h4>Local tip</h4>
            <p className="modal__recommendation">
              <i className="fa-solid fa-lightbulb" aria-hidden="true" />
              <span>{spot.recommendations}</span>
            </p>
          </div>

          <div className="modal__actions">
            <a
              className="btn btn--primary"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa-solid fa-diamond-turn-right" aria-hidden="true" />
              Open in Maps
            </a>
            <button className="btn btn--ghost" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotDetail;